import React, { useState } from 'react';
import axios from 'axios';
import {
  Box,
  Paper,
  Typography,
  Button,
  Card,
  CardContent,
  CardActions,
  Alert,
  LinearProgress,
  Chip,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton
} from '@mui/material';
import {
  Assignment as FormIcon,
  AutoAwesome as AIIcon,
  PictureAsPdf as PDFIcon,
  CheckCircle as CheckIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import Grid from '@mui/material/Grid2';
import { supabase } from '../supabaseClient';

// Styled components
const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: theme.spacing(2),
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
  border: '1px solid rgba(0, 0, 0, 0.05)',
  transition: 'all 0.3s ease-in-out',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 8px 30px rgba(0, 0, 0, 0.12)',
  },
}));

// Form options mapping
const FORM_OPTIONS = [
  { value: 'form_1', label: 'Blue Cross', filename: 'form_1.pdf' },
  { value: 'form_2', label: 'HSBC', filename: 'form_2.pdf' },
  { value: 'form_3', label: 'AIA', filename: 'form_3.pdf' },
  { value: 'form_4', label: 'Bupa', filename: 'form_4.pdf' }
];


const report_sample = `
FULL NAME (BLOCK LETTERS): MEERA PATEL

POLICY NUMBER: POL-2025-456789

MEMBER/CERTIFICATE NO.: MEM-789456123

EMPLOYER NAME (IF APPLICABLE): HONG KONG TECH SOLUTIONS LTD

AGE: 34

GENDER: FEMALE

ID CARD NUMBER: A123456(7)

MOBILE NUMBER: +852 9876-5432

EMAIL: meera.patel@email.com

SYMPTOMS/COMPLAINTS: PAIN CONTROLLED (2/10), TOLERATING LIQUIDS, NO FEVER

SYMPTOMS ONSET DATE: 02/06/2025

DIAGNOSIS: ACUTE NON-PERFORATED APPENDICITIS

ICD-10 CODE: K35.80

HOSPITAL NAME: CANOSSA HOSPITAL

ADMISSION DATE: 03/06/2025

DISCHARGE DATE: 07/06/2025

WARD LEVEL: SEMI-PRIVATE B

SURGERY PERFORMED: YES

DATE OF SURGERY: 04/06/2025

PROCEDURE: LAPAROSCOPIC APPENDECTOMY

CPT CODE: 44970

SURGEON NAME: DR. EDWIN HO

FIRST CONSULTATION DATE: 02/06/2025

DURATION OF SYMPTOMS BEFORE CONSULTATION: 6 HOURS

CLINICAL FINDINGS:- ON 05/06/2025: VITALS: TEMP 36.8°C, HR 72, BP 115/75. LABS: WBC 8.4 (DECREASED FROM 14.2). INCISION: CLEAN, NO ERYTHEMA.- HOSPITAL COURSE: AFEBRILE BY POST-OPERATIVE DAY 1, WBC NORMALIZED.

FINAL DIAGNOSIS: ACUTE NON-PERFORATED APPENDICITIS

RECURRENT EPISODE/CHRONIC ILLNESS: NO

FIRST EPISODE DATE: 02/06/2025

PREVIOUS TREATMENT DETAILS: PAIN MEDICATION AT HOME ON 01/06/2025 BY DR. SARAH LIM, NO PRIOR SURGERY

CONDITION RELATED TO: ACUTE ONSET, NOT WORK-RELATED

DISCHARGE SUMMARY: UNCOMPLICATED RECOVERY POST-LAPAROSCOPIC APPENDECTOMY. PATIENT AFEBRILE BY POST-OPERATIVE DAY 1, WBC NORMALIZED. DISCHARGE PLAN INCLUDES: MEDICATIONS: CEFUROXIME 500MG PO BID FOR 5 DAYS (RX# HK-6655). ACTIVITY: NO HEAVY LIFTING FOR 2 WEEKS. FOLLOW-UP: 17/06/2025 AT SURGICAL CLINIC ROOM 402 (DR. HO).

REASON FOR HOSPITALIZATION (IF OUTPATIENT CARE WAS POSSIBLE): ADMISSION WAS MEDICALLY NECESSARY FOR ACUTE SURGICAL ABDOMEN.

PHYSICIAN'S NAME: DR. EDWIN HO

QUALIFICATIONS: FRCS

CONTACT NUMBER: ‪‪+852 2345-6789‬‬

CLINIC/HOSPITAL ADDRESS: CANOSSA HOSPITAL

PATIENT'S USUAL PHYSICIAN: NOT AVAILABLE

REFERRING DOCTOR (IF APPLICABLE): NOT AVAILABLE

PHYSICIAN'S DECLARATION: THIS ADMISSION WAS MEDICALLY NECESSARY FOR ACUTE SURGICAL ABDOMEN.

`

const PDFFormUploader = ({ medicalData, onClose, extractParagraphText }) => {
  const [selectedForm, setSelectedForm] = useState('');
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleFormSelect = (event) => {
    setSelectedForm(event.target.value);
    setError('');
    setStep(2);
  };

  const handleSubmit = async () => {
    if (!selectedForm || !medicalData || !medicalData.trim()) {
      setError('Please select a form and ensure medical data is provided');
      return;
    }

    setLoading(true);
    setError('');
    setStep(3);
    
    try {
      // Find the selected form details
      const formDetails = FORM_OPTIONS.find(form => form.value === selectedForm);
      
      // Download the PDF from Supabase Storage
      const { data, error: downloadError } = await supabase.storage
        .from('tikcare-attachments') // Your bucket name
        .download(`updated_forms/${formDetails.filename}`);
      
      if (downloadError) {
        throw new Error(`Failed to download form: ${downloadError.message}`);
      }
      
      // Create a File object from the downloaded blob
      const file = new File([data], formDetails.filename, { type: 'application/pdf' });
      const sample = medicalData.includes('<p>') || medicalData.includes('<div>') 
        ? extractParagraphText(medicalData) 
        : medicalData;

      // console.log('Selected form file:', file);
      // console.log('Extracted sample text:', sample);

      // Create FormData for the API call
      const formData = new FormData();
      formData.append('file', file);
      formData.append('sample', sample);
      // formData.append('sample', report_sample || ''); 

      const apiResponse = await axios.post(`${process.env.REACT_APP_MAIN_BACKEND_URL}/fill-pdf-form`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        responseType: 'blob',
      });

      // Create a blob URL from the response
      const resultBlob = new Blob([apiResponse.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(resultBlob);
      
      // Create a temporary anchor element to trigger download
      const link = document.createElement('a');
      link.href = url;
      
      // Get filename from response headers or use default
      const contentDisposition = apiResponse.headers['content-disposition'];
      let filename = `filled_${formDetails.filename}`;
      
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch) {
          filename = filenameMatch[1].replace(/['"]/g, '');
        }
      }
      
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      
      // Clean up
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      setStep(4);
      setSuccess(`${formDetails.label} form filled and downloaded successfully!`);
    } catch (error) {
      console.error('Error filling PDF form:', error);
      
      if (error.response) {
        if (error.response.data instanceof Blob) {
          const reader = new FileReader();
          reader.onload = function() {
            try {
              const errorData = JSON.parse(reader.result);
              setError(errorData.error || 'Server error occurred');
            } catch {
              setError(`Server error: ${error.response.status}`);
            }
          };
          reader.readAsText(error.response.data);
        } else {
          setError(error.response.data?.error || `Server error: ${error.response.status}`);
        }
      } else if (error.request) {
        setError('Network error - please check your connection');
      } else {
        setError(error.message || 'Failed to process PDF form');
      }
      
      setStep(2); // Reset to step 2 on error
    } finally {
      setLoading(false);
    }
  };

  const getStepColor = (stepNumber) => {
    if (stepNumber < step) return 'success';
    if (stepNumber === step) return 'primary';
    return 'default';
  };

  const getSelectedFormLabel = () => {
    const form = FORM_OPTIONS.find(f => f.value === selectedForm);
    return form ? form.label : '';
  };

  return (
    <Box sx={{ display: 'flex' }}>
      <Box component="main" sx={{ flexGrow: 1, p: 3, mt: 2, backgroundColor: '#f8fafc', position: 'relative' }}>
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            top: 16,
            right: 16,
            backgroundColor: 'white',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
            zIndex: 1000,
            '&:hover': {
              backgroundColor: '#f5f5f5',
            }
          }}
        >
          <CloseIcon />
        </IconButton>
        <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
          {/* Header */}
          <Box sx={{ mb: 4, textAlign: 'center' }}>
            <Typography variant="h3" sx={{ fontWeight: 700, color: '#1e293b', mb: 2 }}>
              PDF Form Filler
            </Typography>
            <Typography variant="h6" sx={{ color: '#64748b', mb: 3 }}>
              Select a form template and automatically fill it with your medical data
            </Typography>
            
            {/* Progress Steps */}
            <Stack direction="row" spacing={2} justifyContent="center" sx={{ mb: 3 }}>
              <Chip 
                icon={<FormIcon />} 
                label="Select Form" 
                color={getStepColor(1)}
                variant={step >= 1 ? 'filled' : 'outlined'}
              />
              <Chip 
                icon={<PDFIcon />} 
                label="Review Data" 
                color={getStepColor(2)}
                variant={step >= 2 ? 'filled' : 'outlined'}
              />
              <Chip 
                icon={<AIIcon />} 
                label="AI Processing" 
                color={getStepColor(3)}
                variant={step >= 3 ? 'filled' : 'outlined'}
              />
              <Chip 
                icon={<CheckIcon />} 
                label="Complete" 
                color={getStepColor(4)}
                variant={step >= 4 ? 'filled' : 'outlined'}
              />
            </Stack>
          </Box>

          {/* Alerts */}
          {error && (
            <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
              {success}
            </Alert>
          )}

          <Grid container spacing={3}>
            {/* Left Column - Input */}
            <Grid size={{ xs: 12, lg: 6 }}>
              <Stack spacing={3}>
                {/* Form Selection Card */}
                <StyledCard>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <FormIcon sx={{ color: 'primary.main', mr: 1 }} />
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Step 1: Select Form Template
                      </Typography>
                    </Box>
                    
                    <FormControl fullWidth variant="outlined">
                      <InputLabel id="form-select-label">Choose Form Template</InputLabel>
                      <Select
                        labelId="form-select-label"
                        value={selectedForm}
                        onChange={handleFormSelect}
                        label="Choose Form Template"
                        sx={{
                          borderRadius: 2,
                        }}
                      >
                        {FORM_OPTIONS.map((form) => (
                          <MenuItem key={form.value} value={form.value}>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <PDFIcon sx={{ mr: 1, color: 'primary.main' }} />
                              {form.label}
                            </Box>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    {selectedForm && (
                      <Box sx={{ mt: 2, p: 2, backgroundColor: 'primary.light', borderRadius: 1 }}>
                        <Typography variant="body2" sx={{ color: 'primary.main', fontWeight: 600 }}>
                          Selected: {getSelectedFormLabel()} Form Template
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </StyledCard>

                {/* Medical Data Review Card */}
                <StyledCard>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <PDFIcon sx={{ color: 'primary.main', mr: 1 }} />
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Step 2: Medical Data Ready
                      </Typography>
                    </Box>
                    
                    <Box sx={{ 
                      p: 2, 
                      backgroundColor: 'grey.50', 
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: 'grey.200'
                    }}>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Medical data is ready to be processed:
                      </Typography>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          maxHeight: 150, 
                          overflow: 'auto',
                          whiteSpace: 'pre-wrap',
                          fontSize: '0.875rem'
                        }}
                      >
                        {medicalData ? 
                          <div
                            dangerouslySetInnerHTML={{
                              __html: medicalData.replace(/\*\*(.*?)\*\*/g, '<b>$1</b>').replace(/\n/g, '<br />')
                            }}
                          />
                          : 
                          'No medical data provided'
                        }
                      </Typography>
                    </Box>
                  </CardContent>
                  <CardActions sx={{ px: 3, pb: 3 }}>
                    <Button
                      variant="contained"
                      size="large"
                      fullWidth
                      onClick={handleSubmit}
                      disabled={!selectedForm || !medicalData?.trim() || loading}
                      startIcon={loading ? null : <AIIcon />}
                      sx={{
                        py: 1.5,
                        borderRadius: 2,
                        textTransform: 'none',
                        fontSize: '1.1rem',
                        fontWeight: 600,
                      }}
                    >
                      {loading ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                          <LinearProgress sx={{ flexGrow: 1, mr: 2 }} />
                          Processing...
                        </Box>
                      ) : (
                        'Fill PDF Form'
                      )}
                    </Button>
                  </CardActions>
                </StyledCard>
              </Stack>
            </Grid>

            {/* Right Column - Instructions */}
            <Grid size={{ xs: 12, lg: 6 }}>
              <Stack spacing={3}>
                {/* Instructions Card */}
                <StyledCard>
                  <CardContent>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: 'primary.main' }}>
                      How it works
                    </Typography>
                    <Stack spacing={2}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                        <Typography sx={{ fontWeight: 600, mr: 1, color: 'primary.main' }}>1.</Typography>
                        <Typography variant="body2">
                          Select a form template from our available options (Blue Cross or AIA)
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                        <Typography sx={{ fontWeight: 600, mr: 1, color: 'primary.main' }}>2.</Typography>
                        <Typography variant="body2">
                          Review your medical data that will be used to fill the form
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                        <Typography sx={{ fontWeight: 600, mr: 1, color: 'primary.main' }}>3.</Typography>
                        <Typography variant="body2">
                          AI automatically extracts form fields and matches them with your medical data
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                        <Typography sx={{ fontWeight: 600, mr: 1, color: 'primary.main' }}>4.</Typography>
                        <Typography variant="body2">
                          Completed PDF form is automatically downloaded to your device
                        </Typography>
                      </Box>
                    </Stack>
                  </CardContent>
                </StyledCard>

                {/* Available Forms Card */}
                <StyledCard>
                  <CardContent>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: 'primary.main' }}>
                      Available Form Templates
                    </Typography>
                    <Stack spacing={2}>
                      {FORM_OPTIONS.map((form) => (
                        <Box 
                          key={form.value}
                          sx={{ 
                            display: 'flex', 
                            alignItems: 'center',
                            p: 2,
                            backgroundColor: selectedForm === form.value ? 'primary.light' : 'grey.50',
                            borderRadius: 1,
                            border: '1px solid',
                            borderColor: selectedForm === form.value ? 'primary.main' : 'grey.200'
                          }}
                        >
                          <PDFIcon sx={{ mr: 2, color: 'primary.main' }} />
                          <Box>
                            <Typography variant="body1" sx={{ fontWeight: 600 }}>
                              {form.label}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Template: {form.filename}
                            </Typography>
                          </Box>
                          {selectedForm === form.value && (
                            <CheckIcon sx={{ ml: 'auto', color: 'primary.main' }} />
                          )}
                        </Box>
                      ))}
                    </Stack>
                  </CardContent>
                </StyledCard>
              </Stack>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </Box>
  );
};

export default PDFFormUploader;